/**
 * ChatStartedGraph Component
 * Current-vs-previous-period comparison of chat activity.
 *
 * Consumes the server's adaptive-granularity buckets directly (hour / day /
 * week — analytics.service.ts picks the unit from the window width, max ~60
 * points per series). The old client-side day-of-week re-keying is gone: it
 * silently collapsed weeks 2-N of any window wider than 7 days.
 */

import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Box, useTheme } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TimeSeriesData, TimeSeriesGranularity } from '@/hooks/useAnalytics';
import { format } from 'date-fns';

interface ChatStartedGraphProps {
  data: TimeSeriesData | null;
  loading: boolean;
}

function formatBucket(bucketIso: string, granularity: TimeSeriesGranularity): string {
  const d = new Date(bucketIso);
  if (isNaN(d.getTime())) return bucketIso;
  if (granularity === 'hour') return format(d, 'MMM dd HH:00');
  if (granularity === 'day') return format(d, 'MMM dd');
  return format(d, "'W/o' MMM dd"); // week bucket, labeled by its start date
}

const ChatStartedGraph: React.FC<ChatStartedGraphProps> = ({ data, loading }) => {
  const theme = useTheme();

  // Index-aligned comparison: bucket N of the current period lines up with
  // bucket N of the previous period (both ranges have equal duration and the
  // same granularity, so the arrays correspond position-by-position).
  const chartData = useMemo(() => {
    if (!data?.currentPeriod) return [];
    const prev = data.previousPeriod || [];
    return data.currentPeriod.map((point, i) => ({
      label: formatBucket(point.bucket, data.granularity),
      currentDate: format(new Date(point.bucket), 'dd MMM'),
      previousDate: prev[i] ? format(new Date(prev[i]!.bucket), 'dd MMM') : '',
      currentPeriod: point.messages,
      previousPeriod: prev[i]?.messages ?? 0,
    }));
  }, [data]);

  // Custom tooltip showing both periods' real dates
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const row = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" fontWeight="bold" mb={1}>
            {row.label}
          </Typography>
          {row.currentDate && (
            <Typography variant="body2" color="primary.main" mb={0.5}>
              {row.currentDate}: {row.currentPeriod} messages
            </Typography>
          )}
          {row.previousDate && (
            <Typography variant="body2" color="text.secondary">
              {row.previousDate}: {row.previousPeriod} messages
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Chat Started
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <Typography color="text.secondary">Loading chart data...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!data || chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Chat Started
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" mb={2}>
          Chat Started
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
            <defs>
              <linearGradient id="colorCurrentPeriod" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8} />
                <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorPreviousPeriod" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#9e9e9e" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#9e9e9e" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" angle={-45} textAnchor="end" height={80} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            {/* Top-right: the default bottom placement collided with the
                rotated x-axis labels (legend rendered over 'Wednesday…'). */}
            <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 8 }} />
            <Area
              type="monotone"
              dataKey="currentPeriod"
              name="Current Period"
              stroke={theme.palette.primary.main}
              fillOpacity={1}
              fill="url(#colorCurrentPeriod)"
              strokeWidth={2}
            />
            {/* Lower opacity so the current-period area never fully hides it */}
            <Area
              type="monotone"
              dataKey="previousPeriod"
              name="Previous Period"
              stroke="#9e9e9e"
              fillOpacity={0.35}
              fill="url(#colorPreviousPeriod)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default ChatStartedGraph;
